import { API_PREFIX } from '@hit-game/shared/constants';
import axios, { type AxiosRequestConfig } from 'axios';

import { showToast } from '../context/toast-bus';
import { siteConfig } from './config';

/** Cờ điều khiển interceptor, gắn kèm vào từng request. */
export interface RequestFlags {
  /** Đã thử refresh 1 lần rồi — chặn vòng lặp vô hạn. */
  _retry?: boolean;
  /** Không bắn toast cho request này (vd: poll nền, không nên spam khi mạng chập). */
  _silent?: boolean;
  /**
   * Chỉ tắt toast THÀNH CÔNG, vẫn báo khi lỗi.
   *
   * Dành cho việc chạy nền mà người dùng không hề bấm — như lượt đồng bộ tự động
   * lúc mở hộp thoại lịch sử. Báo "Đồng bộ xong: 0 giao dịch" cho một việc họ
   * không xin là nhiễu, nhưng nuốt luôn lỗi (`_silent`) thì bảng đứng im ở bản cũ
   * mà không ai biết vì sao.
   */
  _silentSuccess?: boolean;
  /**
   * Bỏ qua toàn bộ xử lý 401 (không refresh, không điều hướng) — 401 là kết quả
   * HỢP LỆ của request này.
   *
   * Bắt buộc cho lời gọi dò phiên lúc khởi động: chưa đăng nhập thì `/users/me`
   * trả 401, nếu để interceptor xử lý sẽ thành refresh → refresh cũng 401 →
   * `location.href = '/login'` → tải lại trang → dò phiên lại → LẶP VÔ HẠN.
   */
  _skipAuthHandling?: boolean;
}

export type ApiRequestConfig = AxiosRequestConfig & RequestFlags;

const api = axios.create({
  baseURL: siteConfig.apiUrl,
  headers: { 'Content-Type': 'application/json' },
  // Token nằm trong cookie httpOnly — bắt buộc gửi kèm cookie.
  withCredentials: true,
});

// ─── Response: toast tự động ─────────────────────────────────────────────

api.interceptors.response.use(
  (response) => {
    // GET là đọc dữ liệu, báo "thành công" mỗi lần tải là nhiễu. Chỉ toast cho
    // hành động ghi (POST/PUT/PATCH/DELETE).
    const method = response.config.method?.toLowerCase();
    const flags = response.config as ApiRequestConfig;

    if (method && method !== 'get' && !flags._silent && !flags._silentSuccess) {
      const message = (response.data as { message?: string })?.message;
      if (message) showToast.success(message);
    }
    return response;
  },
  async (error: unknown) => handleResponseError(error),
);

// ─── Response: tự refresh khi 401 ────────────────────────────────────────

/**
 * Nhiều request 401 cùng lúc chỉ được refresh MỘT lần — nếu không, N request
 * song song sẽ gọi N lần `/refresh`, mà rotation khiến lần thứ 2 trở đi dùng
 * token đã revoke → server coi là token bị đánh cắp và thu hồi sạch phiên.
 */
let refreshing: Promise<void> | null = null;

function refreshSession(): Promise<void> {
  refreshing ??= axios
    .post(`${siteConfig.apiUrl}${API_PREFIX}/auths/refresh`, {}, { withCredentials: true })
    .then(() => undefined)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/**
 * Body lỗi theo RFC 9457 (`application/problem+json`) mà server trả về.
 *
 * `type` là thứ để phân nhánh theo LOẠI lỗi — cùng 401 nhưng "sai mật khẩu" và
 * "thiếu token" là hai việc khác nhau, `status` không phân biệt được.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** Định danh để lần lại log của đúng request này ở phía server. */
  requestId?: string;
  errors?: { detail: string; pointer: string }[];
}

interface AxiosLikeError {
  config?: ApiRequestConfig;
  response?: { status?: number; data?: ProblemDetails };
  message?: string;
}

/** Lỗi API đã bóc — giữ nguyên `type`/`requestId` để nơi gọi dùng tiếp. */
export interface ApiError extends Error {
  status?: number;
  problemType?: string;
  requestId?: string;
  fieldErrors?: { detail: string; pointer: string }[];
}

async function handleResponseError(error: unknown): Promise<never> {
  const err = error as AxiosLikeError;
  const status = err.response?.status;
  const request = err.config;

  const isAuthEndpoint =
    request?.url?.includes('/auths/login') || request?.url?.includes('/auths/refresh');

  const shouldHandle401 =
    status === 401 && request && !request._retry && !isAuthEndpoint && !request._skipAuthHandling;

  if (shouldHandle401) {
    request._retry = true;
    try {
      await refreshSession();
      return (await api(request)) as never;
    } catch {
      // Refresh hỏng = phiên chết hẳn. Điều hướng cứng để dọn sạch state React —
      // nhưng chỉ khi đang ở trang khác: đứng sẵn ở /login mà gán lại chính nó
      // là tự tải lại trang thành vòng lặp.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error('Phiên đăng nhập đã hết hạn');
    }
  }

  const problem = err.response?.data;

  // `detail` mô tả đúng lần này, `title` là tên loại lỗi — ưu tiên cái cụ thể hơn.
  const message =
    problem?.detail ?? problem?.title ?? err.message ?? 'Đã có lỗi xảy ra. Vui lòng thử lại.';

  if (!request?._silent) {
    // Chỉ kèm mã tra cứu khi lỗi thuộc về PHÍA SERVER: 4xx thì thông báo đã tự
    // nói rõ phải làm gì, thêm một chuỗi hex chỉ làm rối. Còn 5xx thì người dùng
    // không tự xử được — thứ họ cần là mã để đọc cho người trực.
    const isServerFault = (status ?? 0) >= 500;
    showToast.error(
      isServerFault && problem?.requestId ? `${message} (mã: ${problem.requestId})` : message,
    );
  }

  const enhanced = new Error(message) as ApiError;
  enhanced.status = status;
  enhanced.problemType = problem?.type;
  enhanced.requestId = problem?.requestId;
  enhanced.fieldErrors = problem?.errors;
  throw enhanced;
}

export default api;
